import { Button, Textarea } from '@planuze/ui';
import { Send } from 'lucide-react';
import { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useSupportContent } from '../../content/sections/support.js';
import { QuickActionsMenu } from './quick-actions-menu.js';

interface Props {
  readonly onSend: (content: string) => void;
  readonly onTyping: (isTyping: boolean) => void;
  readonly disabled: boolean;
}

/* Composer texto-puro (upload de attachment via R2 é follow-up). Enter envia;
   Shift+Enter quebra linha. Typing emitido com debounce de 1.5s (anti-spam). */
export function ChatComposer({ onSend, onTyping, disabled }: Props) {
  const copy = useSupportContent();
  const [content, setContent] = useState('');
  const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (typingTimer.current !== null) clearTimeout(typingTimer.current);
    },
    [],
  );

  const send = (): void => {
    const trimmed = content.trim();
    if (trimmed.length === 0) return;
    onSend(trimmed);
    setContent('');
    onTyping(false);
  };

  const handleChange = (value: string): void => {
    setContent(value);
    onTyping(true);
    if (typingTimer.current !== null) clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => onTyping(false), 1500);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      send();
    }
  };

  return (
    <div className="flex items-end gap-2 border-t border-border p-3">
      <QuickActionsMenu
        onSelect={(text) => handleChange(content.length > 0 ? `${content}\n${text}` : text)}
      />
      <Textarea
        value={content}
        rows={2}
        disabled={disabled}
        placeholder={copy.chatComposer.placeholder}
        aria-label={copy.chatComposer.messageAriaLabel}
        className="flex-1 resize-none"
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={handleKeyDown}
      />
      <Button
        size="icon"
        aria-label={copy.chatComposer.sendAriaLabel}
        disabled={disabled || content.trim().length === 0}
        onClick={send}
      >
        <Send className="size-4" aria-hidden="true" />
      </Button>
    </div>
  );
}
